import dayjs from "dayjs";
import { useLayoutEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	ComplexFilterItem,
	ComplexFilterType,
	DateComplexFilter,
	PropertyIdComplexFilter,
} from "interfaces/filters";

import {
	getExpenses,
	getExpensesFilter,
	setExpensesFilter,
} from "state/modules/expenses";

import { getPropertiesList } from "state/modules/properties";
import { SlidersHorizontal } from "lucide-react";
import {
	Badge,
	MultiSelect as UIMultiSelect,
	Button,
	DateRangePicker,
	useIsMobile,
} from "@hotelise/ui";
import type { DateRange, DateRangePreset } from "@hotelise/ui";

import { getDatePresets } from "utils/datePresets";

const ExpensesFilter = (): JSX.Element => {
	const dispatch = useDispatch();
	const filters = useSelector(getExpensesFilter);

	const propertiesList = useSelector(getPropertiesList);
	const properties = useMemo(
		() => propertiesList
			.filter((p) => p.id)
			.map((p) => ({ id: p.id, label: p.name || "-" })),
		[propertiesList]
	);

	const [filtersOpen, setFiltersOpen] = useState(false);
	const [activePreset, setActivePreset] = useState<string | null>(null);
	const [customDateApplied, setCustomDateApplied] = useState(false);
	const isMobile = useIsMobile();
	// Computed per render so relative ranges (Last 30 days / YTD) stay
	// accurate if the app stays open across day boundaries.
	const presets: DateRangePreset[] = getDatePresets();

	const defaultFilters: ComplexFilterItem[] = useMemo(() => {
		const today = dayjs();
		const startOfThisMonth = today.startOf("month");
		const endOfThisMonth = today.endOf("month");

		return [
			{
				type: ComplexFilterType.DATE,
				value: {
					dateFrom: startOfThisMonth.toISOString(),
					dateTo: endOfThisMonth.toISOString(),
				},
			} as DateComplexFilter,
		];
	}, []);

	useLayoutEffect(() => {
		dispatch(setExpensesFilter(defaultFilters));
		dispatch(getExpenses());
	}, [dispatch, defaultFilters]);

	const dateFilter = filters.find((f) => f.type === ComplexFilterType.DATE) as DateComplexFilter;
	const propertyFilter = filters.find((f) => f.type === ComplexFilterType.PROPERTY_ID) as PropertyIdComplexFilter;

	const updateFilter = useCallback(
		(updatedFilter: ComplexFilterItem) => {
			const exists = filters.some((f) => f.type === updatedFilter.type);
			const updatedFilters = exists
				? filters.map((f) => (f.type === updatedFilter.type ? updatedFilter : f))
				: [...filters, updatedFilter];
			dispatch(setExpensesFilter(updatedFilters));
			dispatch(getExpenses());
		},
		[filters, dispatch]
	);

	const handleDateChange = useCallback(
		(range: DateRange) => {
			if (!dateFilter) return;
			setActivePreset(null);
			setCustomDateApplied(true);
			const updatedFilter: DateComplexFilter = {
				...dateFilter,
				value: {
					...dateFilter.value!,
					dateFrom: range.from ? range.from.toISOString() : dateFilter.value?.dateFrom ?? null,
					dateTo: range.to ? range.to.toISOString() : null,
				},
			};
			const updatedFilters = filters.map((f) =>
				f.type === updatedFilter.type ? updatedFilter : f
			);
			dispatch(setExpensesFilter(updatedFilters));
			if (range.from && range.to) {
				dispatch(getExpenses());
			}
		},
		[dateFilter, filters, dispatch]
	);

	const handlePresetClick = useCallback(
		(preset: DateRangePreset) => {
			if (!dateFilter) return;
			setActivePreset(preset.label);
			setCustomDateApplied(false);
			updateFilter({
				...dateFilter,
				value: {
					...dateFilter.value!,
					dateFrom: preset.from.toISOString(),
					dateTo: preset.to.toISOString(),
				},
			});
		},
		[dateFilter, updateFilter]
	);

	const handleReset = useCallback(() => {
		setActivePreset(null);
		setCustomDateApplied(false);
		dispatch(setExpensesFilter(defaultFilters));
		dispatch(getExpenses());
	}, [dispatch, defaultFilters]);

	const hasFilters =
		activePreset !== null ||
		customDateApplied ||
		(propertyFilter?.value ?? null) !== null;

	const handleChangeProperty = (selectedIds: string[]) => {
		if (selectedIds.includes("all") || selectedIds.length === 0) {
			const updatedFilters = filters.filter((f) => f.type !== ComplexFilterType.PROPERTY_ID);
			dispatch(setExpensesFilter(updatedFilters));
			dispatch(getExpenses());
		} else {
			const selectedId = selectedIds[selectedIds.length - 1];
			updateFilter({
				type: ComplexFilterType.PROPERTY_ID,
				value: selectedId,
			} as PropertyIdComplexFilter);
		}
	};

	const selectedPropertyIds = propertyFilter?.value ? [propertyFilter.value] : ["all"];

	const propertyOptions = useMemo(() => [{ id: "all", label: "All Properties" }, ...properties], [properties]);

	const dateRangeValue: DateRange = {
		from: dateFilter?.value?.dateFrom ? new Date(dateFilter.value.dateFrom) : undefined,
		to: dateFilter?.value?.dateTo ? new Date(dateFilter.value.dateTo) : undefined,
	};

	const activeFilterCount = propertyFilter?.value ? 1 : 0;

	if (isMobile) {
		return (
			<div className="flex flex-col gap-2 p-4 bg-card rounded-lg border border-border relative z-10 mb-2">
				<div className="flex flex-wrap gap-1.5">
					{presets.map((preset) => (
						<Badge
							key={preset.label}
							variant={activePreset === preset.label ? "secondary" : "outline"}
							className="cursor-pointer hover:bg-accent transition-colors px-2 py-0.5 text-xs"
							onClick={() => handlePresetClick(preset)}
						>
							{preset.label}
						</Badge>
					))}
				</div>

				<div className="flex items-center gap-2">
					<DateRangePicker
						value={dateRangeValue}
						onChange={handleDateChange}
						presets={presets}
						numberOfMonths={1}
						className="flex-1 w-auto text-xs h-8"
					/>

					<Button
						variant={filtersOpen ? "secondary" : "outline"}
						size="sm"
						className="h-8 px-2.5 shrink-0"
						onClick={() => setFiltersOpen(!filtersOpen)}
					>
						<SlidersHorizontal className="h-3.5 w-3.5" />
						{activeFilterCount > 0 && (
							<span className="ml-1 text-[10px] bg-primary text-primary-foreground rounded-full h-4 w-4 flex items-center justify-center">
								{activeFilterCount}
							</span>
						)}
					</Button>
				</div>

				{filtersOpen && (
					<div className="flex items-center gap-2">
						<UIMultiSelect
							items={propertyOptions}
							selectedItems={selectedPropertyIds}
							onSelectionChange={handleChangeProperty}
							placeholder="Property"
							className="flex-1"
							renderSelectedTags={false}
						/>
						{hasFilters && (
							<Button variant="ghost" size="sm" className="shrink-0" onClick={handleReset}>
								Reset
							</Button>
						)}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-2 p-4 bg-card rounded-lg border border-border relative z-10 mb-2">
			<div className="flex flex-wrap gap-1.5">
				{presets.map((preset) => (
					<Badge
						key={preset.label}
						variant={activePreset === preset.label ? "secondary" : "outline"}
						className="cursor-pointer hover:bg-accent transition-colors px-2 py-0.5 text-xs"
						onClick={() => handlePresetClick(preset)}
					>
						{preset.label}
					</Badge>
				))}
			</div>

			<div className="flex flex-wrap items-start gap-4">
				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Date range</span>
					<div>
						<DateRangePicker
							value={dateRangeValue}
							onChange={handleDateChange}
							presets={presets}
							numberOfMonths={2}
							className="w-[260px]"
						/>
					</div>
				</div>

				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Property</span>
					<UIMultiSelect
						items={propertyOptions}
						selectedItems={selectedPropertyIds}
						onSelectionChange={handleChangeProperty}
						placeholder="Select property..."
						className="w-[200px]"
						renderSelectedTags={false}
					/>
					{selectedPropertyIds[0] !== "all" && (
						<div className="flex flex-wrap gap-1 mt-1 w-[200px]">
							{selectedPropertyIds.filter((id) => id !== "all").map((id) => {
								const label = propertyOptions.find((p) => p.id === id)?.label || id;
								return (
									<Badge key={id} variant="secondary" className="text-xs">
										{label}
										<button type="button" aria-label={`Remove ${label}`} className="ml-1 leading-none hover:opacity-80" onClick={() => handleChangeProperty(["all"])}>&times;</button>
									</Badge>
								);
							})}
						</div>
					)}
				</div>

				{hasFilters && (
					<div className="space-y-1">
						<span className="text-xs font-medium text-muted-foreground">&nbsp;</span>
						<div className="flex items-center h-9">
							<Button variant="ghost" size="sm" onClick={handleReset}>
								Reset
							</Button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ExpensesFilter;
